<template>
  <div class="flex mb-12 iframe-form">
    <div class="w-3/5 content-left-panel">
      <sonar-form-content :embedded="false" :onClick="saveSonar" />
    </div>
    <div class="w-2/5 pl-10 content-right-panel">
      <div
        v-for="difficulty in difficultyLevels"
        :key="difficulty.display"
        class="flex mb-10 -mx-2"
      >
        <div class="w-full content-status">
          <label class="vs-input--label">{{ difficulty.display }} Status</label>
          <v-select
            v-model="difficultyStatus[difficulty.value]"
            :clearable="false"
            :placeholder="`Set ${difficulty.display} status`"
            :options="getSonarAllStatus"
            :reduce="(status) => status.value"
            @input="updateAssessmentStatus(difficulty.value, $event)"
          />
        </div>
      </div>

      <div class="flex mb-10 -mx-2" v-if="isEditing">
        <div class="w-1/2 mx-2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input
            disabled
            :value="getCurrentSonar.modified | dateFormat"
          ></vs-input>
        </div>
        <div class="w-1/2 mx-2 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input
            disabled
            :value="getCurrentSonar.created | dateFormat"
          ></vs-input>
        </div>
      </div>

      <div class="flex -mx-2 action-buttons">
        <vs-button
          class="w-full mx-2"
          color="primary"
          icon-pack="feather"
          @click="saveSonar"
          :disabled="isSaveButtonDisabled"
          >Save</vs-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import sonarFormContent from './forms/SonarFormContent'
import { mapGetters, mapActions } from 'vuex'
import vSelect from 'vue-select'

export default {
  components: {
    sonarFormContent,
    'v-select': vSelect,
  },

  data() {
    return {
      difficultyLevels: [
        { display: 'Novice', value: 'novice' },
        { display: 'Professional', value: 'professional' },
        { display: 'Advanced', value: 'advanced' },
      ],
      difficultyStatus: {
        novice: 'draft',
        professional: 'draft',
        advanced: 'draft',
      },
    }
  },

  async created() {
    if (this.$route && this.$route.params.id) {
      if (this.$route.params.id !== this.getCurrentSonar.id) {
        await this.getSonarToEdit(this.$route.params.id)
      }
      this.loadStatusFromAssessments()
    } else {
      this.resetCurrentSonar()
      this.resetDifficultyStatus()
    }
  },

  computed: {
    ...mapGetters('sonar', ['getCurrentSonar', 'getSonarAllStatus']),
    isEditing() {
      return this.$route.name === 'edit-sonar'
    },
    isSaveButtonDisabled() {
      return (
        !this.getCurrentSonar.SkillArea ||
        !this.getCurrentSonar.SkillArea.Assessments ||
        this.getCurrentSonar.SkillArea.Assessments.length === 0
      )
    },
  },
  methods: {
    ...mapActions('sonar', [
      'getSonarAction',
      'resetCurrentSonar',
      'saveSonarToApi',
    ]),
    resetDifficultyStatus() {
      this.difficultyStatus = {
        novice: 'draft',
        professional: 'draft',
        advanced: 'draft',
      }
    },
    loadStatusFromAssessments() {
      this.resetDifficultyStatus()
      
      if (
        this.getCurrentSonar.SkillArea &&
        this.getCurrentSonar.SkillArea.Assessments
      ) {
        this.getCurrentSonar.SkillArea.Assessments.forEach((assessment) => {
          if (
            assessment.assessmentInfo &&
            assessment.assessmentInfo.difficulty
          ) {
            const difficultyKey = assessment.assessmentInfo.difficulty.toLowerCase()
            const status = assessment.assessmentInfo.status || 'draft'
            
            if (this.difficultyStatus.hasOwnProperty(difficultyKey)) {
              this.difficultyStatus[difficultyKey] = status
            }
          }
        })
      }
    },
    updateAssessmentStatus(difficultyValue, status) {
      if (
        this.getCurrentSonar.SkillArea &&
        this.getCurrentSonar.SkillArea.Assessments
      ) {
        this.getCurrentSonar.SkillArea.Assessments.forEach((assessment) => {
          if (
            assessment.assessmentInfo &&
            assessment.assessmentInfo.difficulty &&
            assessment.assessmentInfo.difficulty.toLowerCase() === difficultyValue.toLowerCase()
          ) {
            assessment.assessmentInfo.status = status || 'draft'
          }
        })
      }
    },
    async getSonarToEdit(sonarId) {
      try {
        this.$vs.loading()
        await this.getSonarAction({ sonarId })
        this.loadStatusFromAssessments()
        this.$vs.loading.close()
      } catch (error) {
        this.showError(error)
      }
    },
    async saveSonar() {
      try {
        this.$vs.loading()
        if (
          this.getCurrentSonar.SkillArea &&
          this.getCurrentSonar.SkillArea.Assessments
        ) {
          this.getCurrentSonar.SkillArea.Assessments.forEach((assessment) => {
            if (
              assessment.assessmentInfo &&
              assessment.assessmentInfo.difficulty
            ) {
              const difficultyKey = assessment.assessmentInfo.difficulty.toLowerCase()
              
              if (this.difficultyStatus.hasOwnProperty(difficultyKey)) {
                assessment.assessmentInfo.status = this.difficultyStatus[difficultyKey] || 'draft'
              } else {
                assessment.assessmentInfo.status = 'draft'
              }
            }
          })
        }
        
        await this.saveSonarToApi()
        if (this.$route.name === 'create-sonar') {
          this.$router.push({
            name: 'edit-sonar',
            params: { id: this.getCurrentSonar.SkillArea.id },
          })
        }

        this.$vs.notify({
          title: 'Success',
          text: 'Sonar assessment saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
      } catch (error) {
        this.showError(error)
      }
      this.$vs.loading.close()
    },
    showError(error) {
      this.$vs.notify({
        title: 'Error',
        text: error.message || 'An error occurred',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
      })
    }
  },

  beforeRouteLeave(to, from, next) {
    if (to.name === 'create-sonar') {
      this.resetCurrentSonar()
      this.resetDifficultyStatus()
    }
    if (to.name === 'edit-sonar') {
      this.getSonarToEdit(to.params.id)
    }
    next()
  },
}
</script>
<style lang="scss" scoped></style>